<template>
  <q-card class="drag-list-container q-pa-md" :class="{ 'view-mode': is_view }">
    <!-- 加载指示器 -->
    <q-inner-loading :showing="isLoading" label="正在加载工艺数据..." label-class="text-teal" />

    <q-card-section class="row q-col-gutter-md">
      <!-- 左侧 - 当前工艺路线 -->
      <div class="col-xs-12 col-lg-6">
        <q-card bordered class="shadow-1">
          <q-card-section class="bg-blue-1">
            <div class="row items-center">
              <div class="text-subtitle1">当前工艺路线</div>
              <q-space />
              <div v-if="isLoading" class="text-caption text-blue-8">
                <q-spinner size="sm" /> 加载中...
              </div>
              <div v-else class="text-caption text-blue-8">
                已选 {{ editList.length }} 个工序
              </div>
              <div v-if="is_view" class="text-caption text-red q-ml-sm">
                <q-icon name="fa-solid fa-lock" size="xs" /> 只读模式
              </div>
            </div>
          </q-card-section>

          <q-card-section>
            <div v-if="editList.length === 0" class="text-center q-pa-lg text-grey">
              <q-icon name="fa-solid fa-circle-info" size="sm" class="q-mr-sm" />
              请从右侧添加工序
            </div>

            <!-- <draggable v-model="editList" item-key="tempId" group="process" :disabled="is_view" @start="onDragStart"
              @end="onDragEnd" :touchStartThreshold="10" // 增加触摸阈值，避免误触 :forceFallback="true" // 强制使用fallback，在移动端更稳定
              :fallbackTolerance="5" // 容差设置 :fallbackClass="'dragging-item'" // 拖拽时的样式类 :ghostClass="'ghost-item'" //
              幽灵元素的样式类 :chosenClass="'chosen-item'" // 选中元素的样式类 class="list-group" handle=".handle">
               -->
            <draggable v-model="editList" item-key="tempId" group="process" :disabled="is_view" @start="onDragStart"
              @end="onDragEnd" :touchStartThreshold="10" :forceFallback="false" :fallbackTolerance="5"
              :fallbackClass="'dragging-item'" :ghostClass="'ghost-item'" :chosenClass="'chosen-item'"
              class="list-group" handle=".handle">
              <template #item="{ element, index }">
                <q-item class="q-my-xs bg-grey-1 rounded-borders shadow-1">
                  <q-item-section avatar>
                    <div class="row items-center">
                      <div class="text-caption text-center q-mt-xs text-blue-grey-7">
                        {{ index + 1 }}
                      </div>
                      <q-icon name="fa-solid fa-list-ol"
                        :class="['handle', is_view ? 'text-grey-4' : 'text-grey-6 cursor-grab']" />
                    </div>
                  </q-item-section>

                  <q-item-section>
                    <div class="text-weight-medium">
                      {{ element.name }}
                      <span v-if="element.description" class="text-caption text-grey-7">
                        - {{ element.description }}
                      </span>
                    </div>
                  </q-item-section>

                  <q-item-section side top v-if="!is_view">
                    <div class="row">
                      <q-btn icon="fa-solid fa-xmark" flat round dense size="sm" color="negative"
                        @click.stop="removeItem(element.tempId)" />
                    </div>
                  </q-item-section>
                </q-item>
              </template>
            </draggable>
          </q-card-section>
        </q-card>
      </div>

      <!-- 右侧 - 全部工序 -->
      <div class="col-xs-12 col-lg-6">
        <q-card bordered class="shadow-1">
          <q-card-section class="bg-green-1">
            <div class="text-subtitle1">全部可用工序</div>
            <div class="text-caption text-green-8">
              共 {{ filteredAllItems.length }} 个可用工序
            </div>
          </q-card-section>

          <q-card-section>
            <div class="row q-col-gutter-md q-mb-md">
              <div class="col-12">
                <q-input v-model="searchTerm" placeholder="搜索工序..." outlined dense clearable :readonly="is_view">
                  <template v-slot:prepend>
                    <q-icon name="fa-solid fa-magnifying-glass" />
                  </template>
                </q-input>
              </div>
            </div>

            <div v-if="filteredAllItems.length === 0" class="text-center q-pa-lg text-grey">
              <q-icon name="fa-solid fa-circle-info" size="sm" class="q-mr-sm" />
              没有找到匹配的工序
            </div>

            <q-list bordered separator class="rounded-borders list-all">
              <q-item v-for="item in filteredAllItems" :key="item.id" class="q-mb-xs" :class="{
                'bg-green-1': isItemSelected(item),
                'disabled-item': is_view
              }" :clickable="!is_view" @dblclick="!is_view && addToEditList(item, true)">
                <q-item-section avatar>
                  <q-icon name="fa-solid fa-angles-right" :color="is_view ? 'grey-5' : 'primary'" />
                </q-item-section>

                <q-item-section>
                  <div class="text-weight-medium">
                    {{ item.name }}
                    <span v-if="item.description" class="text-caption text-grey-7">
                      - {{ item.description }}
                    </span>
                  </div>
                </q-item-section>

                <q-item-section side v-if="!is_view">
                  <!-- 不能重复选择时 设置 
                   :disable="isItemSelected(item)" 
                   :color="isItemSelected(item) ? 'grey-5' : 'primary'"
                   -->
                  <q-btn icon="fa-solid fa-plus" round dense color="primary" @click.stop="addToEditList(item, true)" />
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>
        </q-card>
      </div>
    </q-card-section>
  </q-card>
</template>

<script setup>
import { ref, computed, watch, inject, nextTick, onMounted } from 'vue'
import draggable from 'vuedraggable'
import { useQuasar } from 'quasar'
import { request } from "@/request/api";

const $q = useQuasar()

// 定义组件属性
const props = defineProps({
  // 全部工序数据
  allItems: {
    type: Array,
    required: true,
    default: () => []
  },

  // 当前编辑的工艺路线数据
  editData: {
    type: Array,
    required: true,
    default: () => []
  },

  // 当前组件关联的数据表名
  tableName: {
    type: String,
    required: true,
    default: () => '-'
  },

  // 要保存到目标数据表中，项目id对应的字段名
  saveFieldName: {
    type: String,
    required: true,
    default: () => 'SYS_SAVE_FIELD_NAME'
  },

  // 父级ID
  parentId: {
    type: Number,
    required: true,
    default: () => 0
  },

  // 只读模式
  is_view: {
    type: Boolean,
    default: false
  },

  // 是否通过父组件的扩展功能更新数据
  extUpdated: {
    type: Boolean,
    default: false
  },

  // 是否通过父组件的扩展功能更新数据
  extUpdateData: {
    default: ""
  }
})

// 定义事件 - 使用 v-model 实现双向绑定
const emit = defineEmits(['update:editData'])

// 搜索词
const searchTerm = ref('')

// 编辑列表（左侧）使用 ref 创建响应式变量
const editList = ref([])

// 加载状态
const isLoading = ref(false)

// 过滤后的全部工序（右侧）
const filteredAllItems = computed(() => {
  if (!searchTerm.value) return props.allItems

  const term = searchTerm.value.toLowerCase()

  return props.allItems.filter(item => {
    const nameMatch = item.name?.toLowerCase()?.includes(term) || false
    const descMatch = item.description?.toLowerCase()?.includes(term) || false
    return nameMatch || descMatch
  })
})

// 注入通信对象
const dragListComm = inject('dragListComm')
let parentUpdateParentId = '';
let parentUpdateTableName = '';
// 监听注入的数据
watch(() => dragListComm?.value, async (newVal) => {
  // console.log("🔗 通过 inject 接收到数据:", newVal)
  if (newVal?.updated && newVal.data) {
    parentUpdateParentId = newVal.data;
    parentUpdateTableName = newVal.extItem.open_area;

    // 处理数据
    await loadSavedData();

    // 重置状态
    parentUpdateParentId = '';
    dragListComm.value.updated = false
  }
}, { deep: true })

// 初始化编辑列表
watch(() => props.editData, (newVal) => {
  // 为每个项添加临时唯一ID，用于列表操作
  editList.value = newVal.map((item, index) => ({
    ...item,
    tempId: item.tempId || generateTempId(),
    // 初始化排序顺序
    list_order: index + 1
  }))
}, { immediate: true, deep: true })

// 生成临时唯一ID
const generateTempId = () => {
  return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// 添加工序到编辑列表
const addToEditList = (item, show_notify) => {
  // 创建新项，保存原ID并设置新ID为0
  const newItem = {
    ...item,
    tempId: generateTempId(),
    id: 0, // 设置新ID为0（表示新记录）
    order: editList.value.length + 1,

    // 保存原始ID到指定属性
    [props.saveFieldName]: item.id
  }

  // 添加新项
  editList.value.push(newItem)
  updateOrderAndEmit()

  if (show_notify) {
    $q.notify({
      type: 'positive',
      message: `已添加: ${item.name}`,
      position: 'top',
      timeout: 800
    })
  }
}

// 从编辑列表中移除工序
const removeItem = (tempId) => {
  if (props.is_view) return; // 只读模式不执行移除操作

  const index = editList.value.findIndex(item => item.tempId === tempId)
  if (index !== -1) {
    const itemName = editList.value[index].name
    editList.value.splice(index, 1)
    updateOrderAndEmit()

    $q.notify({
      type: 'info',
      message: `已移除: ${itemName}`,
      position: 'top',
      timeout: 800
    })
  }
}

// 检查工序是否已添加
const isItemSelected = (item) => {
  // 在编辑列表中查找匹配的原始ID
  return editList.value.some(i => i[props.saveFieldName] === item.id)
}

// 拖动结束时更新顺序
// const onDragEnd = () => {
//   if (props.is_view) return; // 只读模式不执行拖拽操作
//   updateOrderAndEmit()
// }

// 更新所有项目的排序顺序并触发更新事件
const updateOrderAndEmit = () => {
  // 更新每个项目的顺序号
  editList.value = editList.value.map((item, index) => ({
    ...item,
    list_order: index + 1
  }))

  emitUpdate()
}

// 发出更新事件 - 直接修改父组件值
const emitUpdate = () => {
  // 移除临时ID后发送给父组件
  const cleanList = editList.value.map(({ tempId, ...rest }) => rest)

  // 使用 v-model 更新父组件数据
  emit('update:editData', cleanList)
}

// 在allItems中查找匹配的工序并模拟添加
const findAndAddProcess = (item) => {
  // 获取查找字段名
  const fieldName = props.saveFieldName;

  // 获取需要查找的值
  const fieldValue = item[fieldName];

  if (!fieldValue) {
    console.warn(`找不到字段值: ${fieldName}`, item);
    return;
  }

  // 在allItems中查找匹配的工序
  const foundItem = props.allItems.find(process => process.id === fieldValue);

  if (foundItem) {
    // 模拟人工添加操作
    addToEditList(foundItem, false);
  } else {
    $q.notify({
      type: 'warning',
      message: `找不到工序ID: ${fieldValue}`,
      position: 'top',
      timeout: 1500
    });
  }
}

const waitForAllItems = () => {
  return new Promise(resolve => {
    // 如果已经有数据，立即 resolve（避免 immediate 导致 stop 未定义的问题）
    if (Array.isArray(props.allItems) && props.allItems.length > 0) {
      resolve();
      return;
    }

    const stop = watch(
      () => props.allItems,
      (val) => {
        if (Array.isArray(val) && val.length > 0) {
          stop();
          resolve();
        }
      },
      { immediate: false } // 不要 immediate:true
    );
  });
};

// 加载后台数据
const loadSavedData = async () => {
  if ((!props.parentId || props.parentId === 0) && (parentUpdateParentId == '')) return;

  // ⭐ 等待 props.allItems 加载完成
  await waitForAllItems();

  isLoading.value = true;
  try {
    const res = await request("get_table", {
      table_name: parentUpdateTableName != '' ? parentUpdateTableName : props.tableName,
      where: 'parent_id = ' + (parentUpdateParentId != '' ? parentUpdateParentId : props.parentId),
      order_by: 'list_order',
    });

    // console.log(res);

    if (res && res.length) {
      // 清空当前编辑列表
      editList.value = [];

      // 遍历后台数据，查找并添加匹配的工序
      res.forEach(item => {
        findAndAddProcess(item);
      });

      // 更新顺序并通知父组件
      updateOrderAndEmit();

      $q.notify({
        type: 'positive',
        message: `已加载${res.length}条工序数据`,
        position: 'top',
        timeout: 1500
      });
    }
  } catch (error) {
    console.error('加载数据失败:', error);
    $q.notify({
      type: 'negative',
      message: '数据加载失败: ' + error.message,
      position: 'top',
      timeout: 3000
    });
  } finally {
    isLoading.value = false;
  }
}

// 监听parentId变化
watch(() => props.parentId, (newVal) => {
  if (newVal && newVal !== 0) {
    loadSavedData();
  } else if (newVal === 0) {
    editList.value = [];
    emit('update:editData', []);
  }
}, { immediate: true });

// 拖拽状态
const isDragging = ref(false)
const dragStartY = ref(0)
const scrollTop = ref(0)

// 拖拽开始
const onDragStart = (event) => {
  isDragging.value = true

  // 记录初始位置
  if (event.originalEvent && event.originalEvent.touches) {
    // 移动端
    dragStartY.value = event.originalEvent.touches[0].clientY
  } else {
    // 桌面端
    dragStartY.value = event.clientY
  }

  // 记录滚动位置
  const listGroup = document.querySelector('.list-group')
  if (listGroup) {
    scrollTop.value = listGroup.scrollTop
  }

  // 添加拖拽激活样式
  document.querySelector('.list-group')?.classList.add('dragging-active')

  // console.log("拖拽开始", event)
}

// 拖拽结束
const onDragEnd = (event) => {
  if (props.is_view) return;

  isDragging.value = false

  // 移除拖拽激活样式
  document.querySelector('.list-group')?.classList.remove('dragging-active')

  // 更新顺序
  updateOrderAndEmit()

  // console.log("拖拽结束", event)
}

// 添加触摸移动处理（防止滚动冲突）
const setupTouchHandling = () => {
  nextTick(() => {
    const listGroup = document.querySelector('.list-group')
    if (!listGroup) return

    let touchStartY = 0
    let isPotentialDrag = false

    listGroup.addEventListener('touchstart', (e) => {
      // 检查是否点击了拖拽手柄
      const handle = e.target.closest('.handle')
      if (handle) {
        touchStartY = e.touches[0].clientY
        isPotentialDrag = true
        e.preventDefault() // 防止滚动
      }
    }, { passive: false })

    listGroup.addEventListener('touchmove', (e) => {
      if (isPotentialDrag) {
        const touchY = e.touches[0].clientY
        const deltaY = Math.abs(touchY - touchStartY)

        // 如果垂直移动超过阈值，认为是拖拽而不是滚动
        if (deltaY > 10) {
          e.preventDefault() // 阻止滚动
        }
      }
    }, { passive: false })

    listGroup.addEventListener('touchend', () => {
      isPotentialDrag = false
    })
  })
}

// 在组件挂载时设置触摸处理
onMounted(() => {
  setupTouchHandling()
})
</script>

<style scoped>
.drag-list-container {
  min-width: 1000px;
  max-width: 1200px;
  border-radius: 1px;
  overflow: hidden;
  padding: 0px;
  margin-bottom: 10px;
  transition: all 0.3s;
}

.drag-list-container.view-mode {
  background-color: #f9f9f9;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.1);
}

.list-group {
  min-height: 400px;
  max-height: 500px;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #ffffff;
}

.list-all {
  min-height: 350px;
  max-height: 450px;
  overflow-y: auto;
}

.handle {
  cursor: grab;
}

.handle:active {
  cursor: grabbing;
}

.q-item {
  border-left: 3px solid #42a5f5;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.q-item:hover {
  background-color: #e3f2fd !important;
}

.text-caption.text-grey-7 {
  display: inline-block;
  margin-left: 8px;
  font-style: italic;
}

.q-card {
  border-radius: 10px;
  overflow: hidden;
}

.bg-blue-1 {
  background: #e3f2fd;
}

.bg-green-1 {
  background: #e8f5e9;
}

.q-card-section {
  padding: 20px;
}

/* 只读模式样式 */
.view-mode .q-card {
  border: 1px solid #e0e0e0;
}

.view-mode .q-item {
  border-left: 3px solid #bdbdbd;
}

.view-mode .q-item:hover {
  background-color: #f5f5f5 !important;
}

.view-mode .bg-blue-1 {
  background: #f5f7fa;
}

.view-mode .bg-green-1 {
  background: #f1f8e9;
}

.disabled-item {
  opacity: 0.8;
  cursor: default !important;
}

.view-mode .q-icon {
  opacity: 0.7;
}

/* 添加移动端优化的样式 */
.drag-list-container {
  /* 确保容器不会阻止滚动 */
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
  /* iOS 平滑滚动 */
}

.list-group {
  /* 移动端优化 */
  min-height: 300px;
  max-height: 60vh;
  /* 使用视口高度限制最大高度 */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  /* iOS 平滑滚动 */

  /* 防止拖拽时页面滚动 */
  &.dragging-active {
    overflow: hidden;
  }
}

/* 拖拽项样式优化 */
.q-item {
  position: relative;
  min-height: 60px;
  /* 增加触摸区域 */
  padding: 12px 16px;

  /* 更好的触摸反馈 */
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  /* 移动端优化 */
  @media (max-width: 768px) {
    min-height: 70px;
    padding: 16px;
  }
}

/* 拖拽手柄优化 */
.handle {
  touch-action: none;
  /* 防止手柄触发滚动 */
  min-width: 44px;
  /* 最小触摸区域 44px */
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    min-width: 48px;
    min-height: 48px;
  }
}

/* 拖拽状态样式 */
.dragging-item {
  transform: rotate(5deg);
  opacity: 0.8;
  z-index: 1000;
  position: relative;
}

.ghost-item {
  opacity: 0.4;
  background-color: #e3f2fd;
}

.chosen-item {
  background-color: #f3e5f5 !important;
}

/* 移动端特定样式 */
@media (max-width: 768px) {
  .drag-list-container {
    min-width: auto;
    max-width: 100%;
    padding: 8px;
  }

  .q-card-section {
    padding: 12px;
  }

  .list-group {
    min-height: 40vh;
    max-height: 50vh;
  }

  /* 调整布局为上下排列 */
  .q-col-gutter-md {
    flex-direction: column;
  }

  .col-6 {
    width: 100%;
    margin-bottom: 16px;
  }
}

/* 左侧列表项高度（与右侧接近） */
.list-group .q-item {
  /* min-height: 40px; */
  /* 默认是 56px，这里缩小很多 */
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

/* 序号那一栏也缩小一点 */
.list-group .q-item-section[avatar] {
  min-width: 32px !important;
}
</style>